/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Dexie from "dexie";
import { TQUtils } from "../../utils/TQUtils";
import TQFont, { tqtypes, TQFontAggregation, TQLogLevel } from "../../public/TQTypes";
import TQInitParams from "../../public/TQInitParam";
import LokiDB from "@coretech/lokidb";
import TQTaskScheduler from "../../controller/TQTaskScheduler";

export enum BrowseTables {
    BROWSE = "browseFonts",
    VARIATION_TAGS = "tagData",
    LAST_FETCHED = "lastFetchedMetadata",
}

export enum PreferenceTables {
    RECENTS = "recentFonts",
    INIT_DETAILS = "initDetails",
    SELECTIONS = "selections",
}

interface BrowseFontData {
    psName: string;
    tkId: string;
    sortIndex: number;
    lang: string;
    supportedLangs: string[];
    premium: boolean;
    display: boolean;
    variationId: string;
    familyName: string[];
    styleName: string[];
    familySlug: string;
    locFamilyName: string[];
    locStyleName: string[];
}

interface TablesLastFetchedMetadata {
    tableLastModified: string;
    tableName: string;
}

interface RecentsData {
    psName: string;
    fontJson: string;
    timestamp: number;
}

interface SelectionsData {
    etag: string;
    psNames: string[];
}

interface InitCacheData {
    userId: string;
    clientId: string;
    locale: string;
}

interface VariationTagTableData {
    tagId: string;
    namespaceId: string;
    tagName: string;
    synonyms: Set<string>;
    displayFont: string;
    fontsList: string[];
}

let indexedDBEnabled = indexedDB != undefined;

/**
 * Let us know if IndexedDB is actually enabled or not
 * @returns boolean
 */
function isIndexedDBEnabled() {
    return indexedDBEnabled;
}

/**
 * Let us know if browse INdexed DB is actually enabled or not
 * @returns boolean
 */
function isBrowseIDBEnabled() {
    return indexedDBEnabled;
}

//
// Declare Database
//
class BrowseFontDatabase extends Dexie {
    public browseFonts: Dexie.Table<BrowseFontData, string>; // id is number in this case
    public lastFetchedMetadata: Dexie.Table<TablesLastFetchedMetadata, string>; // id is number in this case
    public tagData: Dexie.Table<VariationTagTableData, string>;

    public constructor() {
        super("BrowseFontDatabase");
        this.version(1).stores({
            browseFonts: "psName, tkId, lang, variationId, *familyName, familySlug, *locFamilyName, *locStyleName", // * for Multi entry
            tagData: "tagId",
            lastFetchedMetadata: "tableName", // * for Multi entry,
        });

        this.version(2)
            .stores({
                browseFonts: "psName, tkId, lang, variationId, *familyName, familySlug, *locFamilyName, *locStyleName", // * for Multi entry
                tagData: "tagId",
                lastFetchedMetadata: "tableName", // * for Multi entry,
            })
            .upgrade(tx => {
                return tx.table("browseFonts").clear();
            });

        this.version(3)
            .stores({
                browseFonts: "psName, tkId, lang,*supportedLangs, variationId, *familyName, familySlug, *locFamilyName, *locStyleName", // * for Multi entry
                tagData: "tagId",
                lastFetchedMetadata: "tableName", // * for Multi entry,
            })
            .upgrade(tx => {
                return tx.table("browseFonts").clear();
            });

        // this.version(4).stores({
        //     browseFonts: "psName, tkId, *locFamilyName, *locStyleName", // * for Multi entry
        //     lastFetchedMetadata: "tableName" // * for Multi entry
        // }).upgrade (tx => {
        //     return tx.table("browseFonts").toCollection().modify(browseData => {
        //         delete browseData.myData;
        //     });
        // });
        this.browseFonts = this.table(BrowseTables.BROWSE);
        this.tagData = this.table(BrowseTables.VARIATION_TAGS);
        this.lastFetchedMetadata = this.table(BrowseTables.LAST_FETCHED);
        this.open().catch(error => {
            // handle error
            indexedDBEnabled = false;
        }); // Here's where upgrade happens.
    }
}

class PreferenceDatabase extends Dexie {
    public recentFonts: Dexie.Table<RecentsData, string>;
    public initDetails: Dexie.Table<InitCacheData, string>;
    public selections: Dexie.Table<SelectionsData, string>;

    public constructor() {
        super("PreferenceDatabase");
        this.version(1).stores({
            recentFonts: "psName, timestamp", // * for Multi entry,
            initDetails: "userId",
        });
        this.version(2)
            .stores({
                recentFonts: "psName, timestamp", // * for Multi entry,
                initDetails: "userId",
                selections: "etag",
            })
            .upgrade(tx => {
                // ..
            });

        this.version(4)
            .stores({
                recentFonts: "psName, fontJson, timestamp", // * for Multi entry,
                initDetails: "userId",
                selections: "etag",
            })
            .upgrade(tx => {
                // ..
                return tx.table("recentFonts").clear();
            });
        this.recentFonts = this.table(PreferenceTables.RECENTS);
        this.initDetails = this.table(PreferenceTables.INIT_DETAILS);
        this.selections = this.table(PreferenceTables.SELECTIONS);
        this.open().catch(error => {
            // handle error
            indexedDBEnabled = false;
        }); // Here's where upgrade happens.
    }
}

export class TQIdb {
    private _browseDb: BrowseFontDatabase;
    private _prefsDB: PreferenceDatabase;
    private _memBrowseDB: LokiDB;
    private _memPrefsDB: LokiDB;

    public get browseDb(): BrowseFontDatabase {
        return this._browseDb;
    }

    public get prefsDB(): PreferenceDatabase {
        return this._prefsDB;
    }

    constructor() {
        this._browseDb = new BrowseFontDatabase();
        this._prefsDB = new PreferenceDatabase();

        // Fallback in-memory databases
        this._memBrowseDB = new LokiDB("browseDb");
        // this._memBrowseDB.initializePersistence( { persistenceMethod:"memory-storage" });
        this._memBrowseDB.addCollection<BrowseFontData>(BrowseTables.BROWSE);
        this._memBrowseDB.addCollection<VariationTagTableData>(BrowseTables.VARIATION_TAGS);
        this._memBrowseDB.addCollection<TablesLastFetchedMetadata>(BrowseTables.LAST_FETCHED);

        this._memPrefsDB = new LokiDB("prefsDB");
        // this._memBrowseDB.initializePersistence( { persistenceMethod:"memory-storage" });
        this._memPrefsDB.addCollection<RecentsData>(PreferenceTables.RECENTS);
        this._memPrefsDB.addCollection<InitCacheData>(PreferenceTables.INIT_DETAILS);
        this._memPrefsDB.addCollection<SelectionsData>(PreferenceTables.SELECTIONS);
    }

    // Important! A valid last modified time means data is present and last-mod-time is also updated.
    public async getBrowseDBLastModTime(tableName: string) {
        let res: any[] = [];
        try {
            res = this._memBrowseDB.getCollection<TablesLastFetchedMetadata>(BrowseTables.LAST_FETCHED).find({ tableName: { $eq: tableName } });
            if (res.length) {
                // ...
            } else if (true == isBrowseIDBEnabled() && (await this.trySafeOpenDB(this.browseDb))) {
                res = await this.browseDb.lastFetchedMetadata.where("tableName").equals(tableName).toArray();
            }
        } catch (e) {
            // ...
        }

        if (res.length) {
            return res[0].tableLastModified;
        }
        return "";
    }

    public async getBrowseDbTableSize(tableName: string) {
        try {
            const count = await this._memBrowseDB.getCollection(tableName).count();
            if (count || false == isBrowseIDBEnabled()) {
                return count;
            } else if (true == isBrowseIDBEnabled() && (await this.trySafeOpenDB(this.browseDb))) {
                return await this.browseDb.table(tableName).count();
            }
        } catch (e) {
            // ...
        }
        return 0;
    }

    /**
     * Clears off browse DB fully or selected table
     * @param {string} tableName Table name to be cleared. If no table name is provided, full DB will be cleared
     */
    public async clearBrowseDbTables(tableName = "") {
        try {
            if (true == isBrowseIDBEnabled() && (await this.trySafeOpenDB(this._browseDb))) {
                if (tableName == "") {
                    await this._browseDb.table(BrowseTables.BROWSE).clear();
                    await this._browseDb.table(BrowseTables.VARIATION_TAGS).clear();
                    await this._browseDb.table(BrowseTables.LAST_FETCHED).clear();
                } else {
                    await this._browseDb.table(tableName).clear();
                }
            }
            // Always try to clear mem db tables as well
            if (tableName == "") {
                this._memBrowseDB.getCollection(BrowseTables.BROWSE).clear();
                this._memBrowseDB.getCollection(BrowseTables.VARIATION_TAGS).clear();
                this._memBrowseDB.getCollection(BrowseTables.LAST_FETCHED).clear();
            } else {
                this._memBrowseDB.getCollection(tableName).clear();
            }
        } catch (e) {
            // ...
        }
    }

    /**
     * Clears off preference DB fully or selected table
     * @param {string} tableName Table name to be cleared. If no table name is provided, full DB will be cleared
     */
    public async clearPrefsDbTables(tableName = "") {
        try {
            if (false == isIndexedDBEnabled()) {
                if (tableName == "") {
                    this._memPrefsDB.getCollection<RecentsData>(PreferenceTables.RECENTS).clear();
                    this._memPrefsDB.getCollection<InitCacheData>(PreferenceTables.INIT_DETAILS).clear();
                    this._memPrefsDB.getCollection<SelectionsData>(PreferenceTables.SELECTIONS).clear();
                } else {
                    this._memPrefsDB.getCollection(tableName).clear();
                }
            } else if (await this.trySafeOpenDB(this._prefsDB)) {
                if (tableName == "") {
                    await this._prefsDB.initDetails.clear();
                    await this._prefsDB.recentFonts.clear();
                    await this._prefsDB.selections.clear();
                } else {
                    await this._prefsDB.table(tableName).clear();
                }
            }
        } catch (e) {
            // ...
        }
    }

    private async trySafeOpenDB(db: Dexie) {
        if (db.isOpen() == false) {
            await db.open();
        }
        return db.isOpen();
    }

    /**
     * Gets set key and tableName from the prefsDB
     * @param {string} tableName Table name to be queried
     * @param {string} key key to be matched
     */
    public async getPrefsDbInitData(tableName: string, key: string) {
        try {
            if (false == isIndexedDBEnabled()) {
                return this._memPrefsDB.getCollection<InitCacheData>(tableName).findOne({ userId: { $eq: key } });
            } else {
                if (await this.trySafeOpenDB(this._prefsDB)) {
                    return await this.prefsDB.table(tableName).get(key);
                }
            }
        } catch (e) {
            // ...
        }
    }

    /**
     * Sets set key and tableName from the prefsDB
     * @param {string} tableName Table name to be queried
     * @param {string} values values to be inserted
     */
    public async bulkPutPrefsDbData(tableName: string, values: any[]) {
        try {
            await this.clearPrefsDbTables(tableName);
            if (false == isIndexedDBEnabled()) {
                return this._memPrefsDB.getCollection(tableName).insert(values);
            } else {
                if (await this.trySafeOpenDB(this._prefsDB)) {
                    return await this.prefsDB.table(tableName).bulkPut(values);
                }
            }
        } catch (e) {
            // ...
        }
    }

    /**
     * Sets set key and tableName from the prefsDB
     * @param {string} tableName Table name to be queried
     * @param {string} values values to be inserted
     */
    public async bulkPutIDBBrowseDbData(tableName: string, values: any[]) {
        try {
            if (true == isBrowseIDBEnabled()) {
                return await this.browseDb.table(tableName).bulkPut(values);
            }
        } catch (e) {
            // ...
        }
    }

    public async bulkPutBrowseMemDbData(tableName: string, values: any[]) {
        try {
            await this._memBrowseDB.getCollection(tableName).insert(values);
        } catch (e) {
            // ...
        }
    }

    public async commitIDBTime(lastModTime: string, tableName: string) {
        try {
            if (true == isBrowseIDBEnabled() && (await this.trySafeOpenDB(this._browseDb))) {
                this.browseDb.lastFetchedMetadata.put({
                    tableLastModified: lastModTime,
                    tableName: tableName,
                });
            }
        } catch (e) {
            // ...
        }
    }

    public async commitMemDBTime(lastModTime: string, tableName: string) {
        try {
            // Always put in In-memory DB as well
            const table = this._memBrowseDB.getCollection(BrowseTables.LAST_FETCHED);
            table.removeWhere((obj: any) => {
                return obj.tableName == tableName;
            });
            table.insert({
                tableLastModified: lastModTime,
                tableName: tableName,
            });
        } catch (e) {
            // ...
        }
    }

    public async putSelectionsData(key: string, value: string[]) {
        try {
            if (false == isIndexedDBEnabled()) {
                await this.clearPrefsDbTables(PreferenceTables.SELECTIONS);
                this._memPrefsDB.getCollection(PreferenceTables.SELECTIONS).insert({ etag: key, psNames: value });
            } else {
                if (await this.trySafeOpenDB(this._prefsDB)) {
                    await this.prefsDB.selections.clear();
                    await this.prefsDB.selections.put({
                        etag: key,
                        psNames: value,
                    });
                }
            }
        } catch (e) {
            // ...
        }
    }

    public async getSelectionsData() {
        try {
            if (false == isIndexedDBEnabled()) {
                const table = this._memPrefsDB.getCollection<SelectionsData>(PreferenceTables.SELECTIONS);
                const data = table.find()[0];
                return data;
            } else {
                if (await this.trySafeOpenDB(this._prefsDB)) {
                    const res = await this.prefsDB.selections.toArray();
                    return res.length ? res[0] : undefined;
                }
            }
        } catch (e) {
            // ...
        }
    }

    public async dbQueryUp(searchItems: string[], searchKeys: string[], tableName: string, exactMatch: boolean) {
        searchItems = searchItems.filter(entry => entry != undefined && entry != null);
        let results: any[] = [];
        try {
            const lastModTime = await this.getBrowseDBLastModTime(tableName);
            let isCJKUnicodes = false;
            const unicodes = searchItems
                .join("")
                .split("")
                .map(x => x.charCodeAt(0));
            for (const unicode of unicodes) {
                if (TQIdb.isCJKUnicode(unicode)) {
                    isCJKUnicodes = true;
                    break;
                }
            }
            const browseMemTable = await this._memBrowseDB.getCollection(tableName);
            const browseMemTableCount = await browseMemTable.count();
            const isMemDBUsable = browseMemTableCount > 0; // If mem DB has data, prefer it

            if (exactMatch == false && isCJKUnicodes == true && lastModTime != "") {
                const langArray = [tqtypes.Language.SimplifiedChinese, tqtypes.Language.TraditionalChinese, tqtypes.Language.Japanese, tqtypes.Language.Korean];
                let filterFn: any = undefined;
                for (const key of searchKeys) {
                    switch (key) {
                        case "locFamilyName": {
                            filterFn = (x: any) => {
                                return x.locFamilyName.join("#").indexOf(searchItems[0]) != -1 || x.locFamilyName.join("#").indexOf(searchItems[0]) != -1;
                            };
                            break;
                        }

                        case "familyName": {
                            filterFn = (x: any) => {
                                return x.familyName.join("#").indexOf(searchItems[0]) != -1 || x.familyName.join("#").indexOf(searchItems[0]) != -1;
                            };
                            break;
                        }

                        case "locStyleName": {
                            filterFn = (x: any) => {
                                return x.locStyleName.join("#").indexOf(searchItems[0]) != -1 || x.locStyleName.join("#").indexOf(searchItems[0]) != -1;
                            };
                            break;
                        }
                    }

                    if (filterFn) {
                        if (isMemDBUsable) {
                            const lang = browseMemTable.find({ lang: { $in: langArray } } as any).filter(filterFn);
                            results = results.concat(lang);
                            const supportedLangs = browseMemTable
                                .find({
                                    supportedLangs: { $containsAny: langArray },
                                } as any)
                                .filter(filterFn);
                            results = results.concat(supportedLangs);
                        } else {
                            if (await this.trySafeOpenDB(this.browseDb)) {
                                results = results.concat(await this.browseDb.table(tableName).where("lang").anyOf(langArray).filter(filterFn).toArray());

                                results = results.concat(await this.browseDb.table(tableName).where("supportedLangs").anyOf(langArray).filter(filterFn).toArray());
                            }
                        }
                    }
                }
                return results;
            }

            if (lastModTime != "") {
                try {
                    if (searchItems.length == 0 && searchKeys.length == 0) {
                        if (isMemDBUsable) {
                            return this._memBrowseDB.getCollection(tableName).find();
                        } else {
                            return await this.browseDb.table(tableName).toArray();
                        }
                    }

                    for (const key of searchKeys) {
                        if (exactMatch) {
                            if (false == isMemDBUsable) {
                                if (await this.trySafeOpenDB(this.browseDb)) {
                                    results = results.concat(await this.browseDb.table(tableName).where(key).anyOf(searchItems).distinct().toArray());
                                }
                            } else {
                                if (key == "psName" || key == "familySlug" || key == "variationId" || key == "tkId" || key == "lang") {
                                    // Single entry keys
                                    results = results.concat(
                                        browseMemTable.find({
                                            [key]: { $in: searchItems },
                                        } as any)
                                    );
                                } else {
                                    results = results.concat(
                                        browseMemTable.find({
                                            [key]: { $containsAny: searchItems },
                                        } as any)
                                    );
                                }
                            }
                        } else {
                            if (false == isMemDBUsable) {
                                if (await this.trySafeOpenDB(this.browseDb)) {
                                    results = results.concat(await this.browseDb.table(tableName).where(key).startsWithAnyOfIgnoreCase(searchItems).distinct().toArray());
                                }
                            } else {
                                results = results.concat(
                                    browseMemTable.find({
                                        [key]: { $regex: [searchItems, "i"] },
                                    })
                                );
                            }
                        }
                    }
                } catch (e) {
                    // ...
                    console.error(e);
                }
            }
            if (searchKeys.length > 1) {
                results = TQUtils.arrayUnique(results);
            }
        } catch (e) {
            // ...
        }
        return results;
    }

    public async getRecentListSorted(maxCount: number) {
        let values: RecentsData[] = [];
        try {
            if (false == isIndexedDBEnabled()) {
                values = this._memPrefsDB
                    .getCollection<RecentsData>(PreferenceTables.RECENTS)
                    .chain()
                    .find()
                    .sort((a, b) => {
                        return b.timestamp - a.timestamp;
                    })
                    .limit(maxCount)
                    .data();
            } else {
                if (await this.trySafeOpenDB(this.prefsDB)) {
                    const ret = await this.prefsDB.recentFonts.orderBy("timestamp").reverse().limit(maxCount);
                    values = await ret.toArray();
                }
            }
        } catch (e) {
            // ..
        }
        return values;
    }

    public async getRecentListRaw(maxCount: number) {
        let recentsList: RecentsData[] = [];
        try {
            if (false == isIndexedDBEnabled()) {
                recentsList = this._memPrefsDB
                    .getCollection<RecentsData>(PreferenceTables.RECENTS)
                    .chain()
                    .find()
                    .sort((a, b) => {
                        return b.timestamp - a.timestamp;
                    })
                    .limit(maxCount)
                    .data();
            } else {
                if (await this.trySafeOpenDB(this.prefsDB)) {
                    const ret = await this.prefsDB.recentFonts.orderBy("timestamp").reverse().limit(maxCount);
                    recentsList = await ret.toArray();
                }
            }
        } catch (e) {
            // ..
        }
        return recentsList;
    }

    // ======================================================================================================================
    // Middleware functions, work irrespective of the bottom layer whether IDB or in-memory
    // These Work on top of the storage dependent functions
    // ======================================================================================================================

    public async clearInitCacheIfNeeded(userId: string, appId: string, isAnonymous: boolean) {
        let isCacheCleared = false;
        try {
            const ret = await this.getPrefsDbInitData(PreferenceTables.INIT_DETAILS, userId);
            const locale = await TQUtils.getAppropriateLocale();
            if (ret) {
                // Found ok,
                if (ret.locale != locale) {
                    // check for locale / userId if not matches
                    await this.clearBrowseDbTables(BrowseTables.VARIATION_TAGS);
                    await this.bulkPutPrefsDbData(PreferenceTables.INIT_DETAILS, [{ userId: userId, clientId: appId, locale: locale }]);
                    isCacheCleared = true;
                }
            } else {
                // User changed
                // Clean others
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, "Clearing prefs as changed");
                if (isAnonymous) {
                    await this.clearPrefsDbTables(PreferenceTables.INIT_DETAILS);
                    await this.bulkPutPrefsDbData(PreferenceTables.INIT_DETAILS, [{ userId: userId, clientId: appId, locale: locale }]);
                } else {
                    await this.clearPrefsDbTables();
                    await this.clearBrowseDbTables(BrowseTables.VARIATION_TAGS); // Not sure which locale data was cached
                    await this.bulkPutPrefsDbData(PreferenceTables.INIT_DETAILS, [{ userId: userId, clientId: appId, locale: locale }]);
                }
                isCacheCleared = true;
            }
        } catch (e) {
            // ..
        }
        return isCacheCleared;
    }

    public async putTagDataInDB(lastModTime: string, values: VariationTagTableData[]) {
        try {
            await this.clearBrowseDbTables(BrowseTables.VARIATION_TAGS);

            await this.bulkPutBrowseMemDbData(BrowseTables.VARIATION_TAGS, values);
            await this.commitMemDBTime(lastModTime, BrowseTables.VARIATION_TAGS);

            this.bulkPutIDBBrowseDbData(BrowseTables.VARIATION_TAGS, values).then(() => {
                this.commitIDBTime(lastModTime, BrowseTables.VARIATION_TAGS);
            });
        } catch (e) {
            // ...
        }
    }

    public static isCJKUnicode(unicode: number) {
        /*
            # CJK Radicals Supplement (2E80–2EFF)
            # Kangxi Radicals (2F00–2FDF)
            # Ideographic Description Characters (2FF0–2FFF)
            # CJK Symbols and Punctuation (3000–303F)
            # Hiragana (3040–309F)
            # Katakana (30A0–30FF)
            # Bopomofo (3100–312F)
            # Hangul Compatibility Jamo (3130–318F)
            # Kanbun (3190–319F)
            # Bopomofo Extended (31A0–31BF)
            # CJK Strokes (31C0–31EF)
            # Katakana Phonetic Extensions (31F0–31FF)
            # Enclosed CJK Letters and Months (3200–32FF)
            # CJK Compatibility (3300–33FF)
            # CJK Unified Ideographs Extension A (3400–4DBF)
            # Yijing Hexagram Symbols (4DC0–4DFF)
            # CJK Unified Ideographs (4E00–9FFF)
            # Yi Syllables (A000–A48F)
            # Yi Radicals (A490–A4CF)
        */

        if (
            (unicode >= 0x3300 && unicode <= 0x33ff) ||
            (unicode >= 0xfe30 && unicode <= 0xfe4f) ||
            (unicode >= 0xf900 && unicode <= 0xfaff) ||
            (unicode >= 0x3040 && unicode <= 0x309f) ||
            (unicode >= 0x30a0 && unicode <= 0x30ff) ||
            (unicode >= 0x2e80 && unicode <= 0x2eff) ||
            (unicode >= 0x4e00 && unicode <= 0x9fff) ||
            (unicode >= 0x3400 && unicode <= 0x4dbf) ||
            (unicode >= 0x20000 && unicode <= 0x2a6df) ||
            (unicode >= 0x2a700 && unicode <= 0x2b73f) ||
            (unicode >= 0x2b740 && unicode <= 0x2b81f) ||
            (unicode >= 0x2b820 && unicode <= 0x2ceaf) ||
            (unicode >= 0x2f800 && unicode <= 0x2fa1f)
        ) {
            // it's cjk string
            return true;
        }
        return false;
    }

    public async processTagCatalogFiles(fileResults: any[]) {
        const dbTags: Map<string, VariationTagTableData> = new Map<string, VariationTagTableData>();
        let index = 0;
        for (const entry of fileResults) {
            index = index + 1;
            // .. process file one by one
            if (entry.file) {
                const lines = (await (entry.file as Blob).text()).split("\n");
                for (const line of lines) {
                    try {
                        const json = JSON.parse(line);
                        switch (index) {
                            case 1:
                                {
                                    const tagId: string = json["id"];
                                    const data: VariationTagTableData = {
                                        tagId: tagId,
                                        tagName: json["name"],
                                        displayFont: "",
                                        synonyms: new Set<string>(),
                                        namespaceId: json["ns_id"],
                                        fontsList: [],
                                    };
                                    data.tagId = tagId;
                                    dbTags.set(tagId, data);
                                }
                                break;

                            case 2:
                                {
                                    const tagId: string = json["tag_id"];
                                    const tag: VariationTagTableData | undefined = dbTags.get(tagId);
                                    if (tag) {
                                        for (const font of json["font_ids"]) {
                                            tag.fontsList.push(String(font));
                                        }
                                        tag.displayFont = json["rep_font_id"];
                                    }
                                }
                                break;

                            case 3:
                                {
                                    const tagId: string = json["id"];
                                    const tag: VariationTagTableData | undefined = dbTags.get(tagId);
                                    if (tag) {
                                        const synonyms: string[] = json["synonyms"];
                                        for (const synonym of synonyms) {
                                            tag.synonyms.add(synonym);
                                        }
                                    }
                                }
                                break;
                        }
                    } catch (e) {
                        // ...
                    }
                }
            }
        } // Promise iteration ended.
        return dbTags;
    }

    private async setIsActiveForUserIfApplicable(aggregation: TQFontAggregation) {
        for (const [familyname, family] of aggregation.getEntries()) {
            for (const font of family.getEntries()) {
                for (const scheduler of TQTaskScheduler._schedulersList) {
                    if (scheduler.cachedFonts().has(font.postscriptName)) {
                        font.isActiveForUser = true;
                        break;
                    }
                }
            }
        }
    }

    public async getAggregationFromFuzzySearch(searchItems: string[], aggregation: TQFontAggregation, appEntitlement: tqtypes.AppEntitlement, userEntitlement: tqtypes.Entitlement) {
        // const time = performance.now();
        let results = await this.dbQueryUp(searchItems.slice(0, 1), ["familyName", "locFamilyName"], BrowseTables.BROWSE, false /* No exact match */);

        results = results.concat(await this.dbQueryUp(searchItems.slice(0, 1), ["locStyleName"], BrowseTables.BROWSE, false /* No exact match */));
        const filterer = (results: any[]) => {
            results = results.filter(entry => {
                for (let searchItem of searchItems.slice(1)) {
                    searchItem = searchItem.toLowerCase();
                    if (entry.locFamilyName.join("#").indexOf(searchItem) == -1 && entry.locStyleName.join("#").indexOf(searchItem) == -1 && entry.psName.indexOf(searchItem) == -1) {
                        return false;
                    }
                }
                return true;
            });
        };
        if (searchItems.length > 1) {
            filterer(results);
        } else {
            results = await this.dbQueryUp(searchItems.slice(0, 1), ["psName"], BrowseTables.BROWSE, false /* No exact match */);
            if (searchItems.length > 1) {
                filterer(results);
            }
        }

        results.splice(1000);
        TQUtils.getFamiliesFromBulkDBData(results, aggregation, appEntitlement, userEntitlement, false);
        this.setIsActiveForUserIfApplicable(aggregation);
    }

    public async getAggregationFromLanguageSearch(searchItems: string[], aggregation: TQFontAggregation, userEntitlement: tqtypes.Entitlement, appEntitlement: tqtypes.AppEntitlement) {
        try {
            const results = await this.dbQueryUp(searchItems, ["supportedLangs"], BrowseTables.BROWSE, true /* No exact match */);
            TQUtils.getFamiliesFromBulkDBData(results, aggregation, appEntitlement, userEntitlement, false);
            this.setIsActiveForUserIfApplicable(aggregation);
        } catch (e) {
            // ..
        }
    }

    public async getFontDataFromVariationId(searchItems: string[], type: tqtypes.FontType = tqtypes.FontType.DEFAULT) {
        const map: Map<string, TQFont> = new Map<string, TQFont>();
        const results = await this.dbQueryUp(searchItems, ["variationId"], BrowseTables.BROWSE, true /* Exact match */);
        for (const fontData of results) {
            const font = TQUtils.createFontFromDBData(fontData, type);
            if (font.postscriptName != "") {
                map.set(font.postscriptName, font);
                for (const scheduler of TQTaskScheduler._schedulersList) {
                    if (scheduler.cachedFonts().has(font.postscriptName)) {
                        font.isActiveForUser = true;
                        break;
                    }
                }
            }
        }
        return map;
    }

    private async executorDisplayFont(_entry: VariationTagTableData) {
        const repFontMap = await this.getFontDataFromVariationId([_entry.displayFont]);
        let font = new TQFont();
        for (const repFont of repFontMap.values()) {
            font = repFont;
            break; // one only
        }
        if (_entry.tagId == "ornaments") {
            font = new TQFont(); // Override ornaments
        }
        return font;
    }

    private async executorAggregation(_entry: VariationTagTableData, appEnt: tqtypes.AppEntitlement, userEnt: tqtypes.Entitlement) {
        const fontsMap = await this.getFontDataFromVariationId(_entry.fontsList);
        let fonts: TQFont[] = Array.from(fontsMap.values());
        if (appEnt == tqtypes.AppEntitlement.PAID) {
            // If paid app, exclude premium fonts for FREE user
            fonts = fonts.filter(entry => {
                if (userEnt == tqtypes.Entitlement.TRIAL && entry.isPremiumFont == true) {
                    return false;
                }
                return true;
            });
        }
        return TQUtils.getAggregationFromFonts(fonts);
    }

    public async getVariationDataFromFetchedDBData(dbTags: VariationTagTableData[], appEntitlement: tqtypes.AppEntitlement, userEntitlement: tqtypes.Entitlement) {
        const tags: Map<string, tqtypes.VariationTag> = new Map<string, tqtypes.VariationTag>();
        for (const dbTag of dbTags) {
            const tag: tqtypes.VariationTag = new tqtypes.VariationTag(dbTag.tagId, dbTag.tagName);
            tags.set(dbTag.tagId, tag);
            dbTag.fontsList.push(dbTag.displayFont);

            const executorDisplayFont = async () => {
                return await this.executorDisplayFont(dbTag);
            };
            const executorAggregation = async () => {
                return await this.executorAggregation(dbTag, appEntitlement, userEntitlement);
            };
            tag.executorDisplayFontInternal = executorDisplayFont;
            tag.executorAggregatorInternal = executorAggregation;
            tag.synonyms = dbTag.synonyms;
        }
        return new Map(
            [...tags.entries()].sort((a, b) => {
                return a[1].tagName.localeCompare(b[1].tagName);
            })
        );
    }
}
